import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Paper,
} from "@mui/material";

const GenerateBibleSurvey = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    bookId: "",
    language: "amharic",
    forceRefresh: true,
  });

  const [loading, setLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (!formData.bookId) {
      setError("Book ID is required");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/bible-survey/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bookId: formData.bookId,
          language: formData.language,
          forceRefresh: formData.forceRefresh,
        }),
      });

      const result = await res.json();

      if (result.success) {
        setGeneratedData(result.data);
        setSuccess(
          result.cached
            ? "Loaded from cache"
            : "AI content generated successfully",
        );
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error(err);
      setError("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Generate Bible Survey
      </Typography>

      <form onSubmit={handleGenerate}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Book ID"
              name="bookId"
              value={formData.bookId}
              onChange={handleChange}
              fullWidth
              required
              placeholder="jude"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Language"
              name="language"
              value={formData.language}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.forceRefresh}
                  onChange={handleChange}
                  name="forceRefresh"
                />
              }
              label="Force Refresh AI Generation"
            />
          </Grid>

          <Grid item xs={12}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Generate Survey"}
            </Button>
          </Grid>
        </Grid>
      </form>

      {generatedData && (
        <Paper
          elevation={3}
          sx={{
            mt: 4,
            p: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Generated Result
          </Typography>

          <Typography>
            <b>Book:</b> {generatedData.bookName}
          </Typography>

          <Typography>
            <b>Book Amharic:</b> {generatedData.bookNameAm}
          </Typography>

          <Typography>
            <b>Language:</b> {generatedData.language}
          </Typography>

          <Typography>
            <b>Provider:</b> {generatedData.provider}
          </Typography>

          <Typography>
            <b>Model:</b> {generatedData.model}
          </Typography>

          <Typography>
            <b>Version:</b> {generatedData.version}
          </Typography>

          <Typography>
            <b>Verified:</b> {generatedData.verified ? "Yes" : "No"}
          </Typography>

          <Typography>
            <b>User Edited:</b> {generatedData.isUserEdited ? "Yes" : "No"}
          </Typography>

          <TextField
            label="Generated Content JSON"
            multiline
            rows={25}
            fullWidth
            margin="normal"
            value={JSON.stringify(generatedData.content, null, 2)}
            InputProps={{
              readOnly: true,
            }}
          />
        </Paper>
      )}

      <Snackbar
        open={!!error}
        message={error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      />

      <Snackbar
        open={!!success}
        message={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      />
    </Box>
  );
};

export default GenerateBibleSurvey;
